import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { resolveUser, ROLES } from './auth';
import { configureLocale } from '../i18n';

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
};

export const SessionProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [profile, setProfile] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [settings, setSettings] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);

  // Which signed-in user the state on screen belongs to. A token refresh for
  // that same user must not blank the app with a spinner; a sign-in as someone
  // new must wait for their profile.
  const resolvedUserId = useRef(null);

  const clearSession = useCallback(() => {
    resolvedUserId.current = null;
    setAuthenticated(false);
    setRole(null);
    setProfile(null);
    setBlocked(false);
    setPendingApproval(false);
    setRejected(false);
    setLoading(false);
  }, []);

  const load = useCallback(async (authUser) => {
    if (!authUser) {
      clearSession();
      return;
    }

    // Until the profile is resolved, a freshly signed-in user is "loading", not
    // "signed in without a profile". This used to mark them authenticated first
    // and fetch the role afterwards, and on a login `loading` was already false
    // — so for a moment RequireAuth saw exactly "authenticated, no role" and
    // showed "Tu cuenta todavía no está habilitada", a few hundred milliseconds
    // before the profile arrived and replaced it.
    if (resolvedUserId.current !== authUser.id) setLoading(true);

    let resolved = await resolveUser(authUser.id);

    // No role yet: this is either an invited professional signing in for the
    // first time, or a patient who registered while email confirmation was
    // pending and so has no profile row yet.
    if (!resolved.role && !resolved.blocked) {
      // Both claims read server-side state, so they work regardless of which
      // device or browser opened the confirmation email.
      const { data: therapistClaim } = await supabase.rpc('claim_therapist_profile');
      if (therapistClaim?.claimed) {
        resolved = await resolveUser(authUser.id);
      } else {
        const { data: patientClaim } = await supabase.rpc('claim_patient_profile');
        if (patientClaim?.claimed) {
          resolved = await resolveUser(authUser.id);
        } else {
          // A professional who applied on their own; the record is created
          // pending and stays inert until admin approves.
          const { data: requestClaim } = await supabase.rpc('claim_professional_request');
          if (requestClaim?.claimed) resolved = await resolveUser(authUser.id);
        }
      }
    }

    resolvedUserId.current = authUser.id;
    // Authenticated arrives with its role, and loading ends last, so no render
    // ever sees a signed-in user without the profile that explains them — even
    // where these updates are not batched into a single render.
    setAuthenticated(true);
    setRole(resolved.role);
    setProfile(resolved.profile);
    setBlocked(Boolean(resolved.blocked));
    setPendingApproval(Boolean(resolved.pendingApproval));
    setRejected(Boolean(resolved.rejected));
    setLoading(false);
  }, [clearSession]);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (active) load(data?.session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'SIGNED_OUT') {
        // Also clears pending and rejected, which used to outlive the user they
        // described and briefly colour whoever signed in next.
        clearSession();
      } else if (session?.user) {
        load(session.user);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [load, clearSession]);

  // Practice-wide locale, timezone and currency drive all formatting.
  useEffect(() => {
    if (!role) return;
    supabase
      .from('practice_settings')
      .select('*')
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setSettings(data);
        configureLocale({
          locale: data.locale,
          timezone: data.default_timezone,
          currency: data.currency_code,
        });
      });
  }, [role]);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    await load(data?.session?.user ?? null);
  }, [load]);

  const value = useMemo(() => ({
    loading,
    role,
    // Signed in but role-less is a real state: an invited professional who has
    // not been recorded yet, or a stranger who signed up. It needs explaining,
    // not a silent bounce to the login page.
    authenticated,
    pendingApproval,
    rejected,
    profile,
    blocked,
    settings,
    refresh,
    isAdmin: role === ROLES.ADMIN,
    isTherapist: role === ROLES.THERAPIST || role === ROLES.ADMIN,
    isPatient: role === ROLES.PATIENT,
    // Admin is also a practising professional, so most professional-scoped
    // queries key off this rather than the role directly.
    therapistId: role === ROLES.ADMIN || role === ROLES.THERAPIST ? profile?.id : null,
    accountId: role === ROLES.PATIENT ? profile?.id : null,
  }), [loading, role, profile, blocked, settings, refresh, authenticated, pendingApproval, rejected]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
